import { AbstractControl, FormControl } from '@angular/forms';

import { EmployeeSummaryDTO, RoomSummaryDTO, SpaProductResourceCountDTO, SpaSimpleProductDTO } from '@api';

export function computeResourceAssignmentModes(product: SpaSimpleProductDTO | undefined): {
  employeeAssignmentMode: SpaProductResourceCountDTO;
  roomAssignmentMode: SpaProductResourceCountDTO;
} {
  return {
    employeeAssignmentMode: product?.numEmployees ?? SpaProductResourceCountDTO.ONE_PER_GUEST,
    roomAssignmentMode: product?.numRooms ?? SpaProductResourceCountDTO.ONE_PER_GUEST,
  };
}

export function computeDisabledResourceIds(
  assignmentMode: SpaProductResourceCountDTO,
  ownId: string | undefined,
  selectedIds: string[],
): Record<string, boolean> {
  if (assignmentMode !== SpaProductResourceCountDTO.ONE_PER_GUEST) {
    return {};
  }

  return Object.fromEntries(selectedIds.filter((id) => id !== ownId).map((id) => [id, true]));
}

export function collectSelectedResourceIds<T>(
  controls: AbstractControl<T>[],
  accessor: (value: T) => string | undefined,
): string[] {
  return controls.map((control) => accessor(control.value)).filter((id): id is string => !!id);
}

export function computeDuplicateRoomWarnings<T extends { room?: RoomSummaryDTO; enabled?: boolean }>(
  controls: AbstractControl<Partial<T>>[],
  roomAssignmentMode: SpaProductResourceCountDTO,
  duplicateMsg: string,
  currentWarnings: Map<number, string>,
  skipDisabled = true,
): Map<number, string> {
  const warningsMap = new Map(currentWarnings);

  if (roomAssignmentMode !== SpaProductResourceCountDTO.ONE_PER_GUEST) {
    controls.forEach((control, index) => {
      if (control.value.room?.roomId || warningsMap.get(index) === duplicateMsg) {
        warningsMap.delete(index);
      }
    });

    return warningsMap;
  }

  const roomCounts = new Map<string, number>();
  controls.forEach((control) => {
    const value = control.value;
    const roomId = value.room?.roomId;
    if (roomId && (!skipDisabled || value?.enabled !== false)) {
      roomCounts.set(roomId, (roomCounts.get(roomId) ?? 0) + 1);
    }
  });

  controls.forEach((control, index) => {
    const roomId = control.value.room?.roomId;
    if (roomId && (roomCounts.get(roomId) ?? 0) > 1) {
      warningsMap.set(index, duplicateMsg);
    } else if (roomId || warningsMap.get(index) === duplicateMsg) {
      warningsMap.delete(index);
    }
  });

  return warningsMap;
}

export function shouldApplyPreferredRoom(
  isSource: boolean,
  modes: { employeeAssignmentMode: SpaProductResourceCountDTO; roomAssignmentMode: SpaProductResourceCountDTO },
): boolean {
  if (isSource) {
    return true;
  }

  const isSharedRoom = modes.roomAssignmentMode === SpaProductResourceCountDTO.ONE;
  const isSharedEmployee = modes.employeeAssignmentMode === SpaProductResourceCountDTO.ONE;
  return !isSharedRoom && !isSharedEmployee;
}

export function syncCompanionDetails<T extends { employee?: EmployeeSummaryDTO; room?: RoomSummaryDTO }>(
  mainGuestDetail: Partial<T> | undefined,
  companionControls: FormControl<Partial<T>>[],
  modes: {
    employeeAssignmentMode: SpaProductResourceCountDTO;
    roomAssignmentMode: SpaProductResourceCountDTO;
  },
): void {
  if (!mainGuestDetail || companionControls.length === 0) {
    return;
  }

  const syncEmployee = modes.employeeAssignmentMode === SpaProductResourceCountDTO.ONE;
  const syncRoom = modes.roomAssignmentMode === SpaProductResourceCountDTO.ONE;

  companionControls.forEach((control) => {
    const patch: Partial<T> = {};
    if (syncEmployee) {
      patch.employee = mainGuestDetail.employee;
    }
    if (syncRoom) {
      patch.room = mainGuestDetail.room;
    }
    if (Object.keys(patch).length > 0) {
      control.patchValue({ ...control.value, ...patch });
    }
  });
}
